/**
 * What the host app hands the alert panel (task 191b; bluesphere-ticketing
 * design §6.2, K4). The panel renders inside MDR's own alert screen and on
 * the Tickets app's ticket screen, so the two things that differ between
 * them come from the host rather than from MDR's app:
 *
 * - **the token**: a hook returning the bearer token for MDR's API, '' before
 *   sign-in. MDR passes its own `useAccessToken`; the Tickets app passes the
 *   user's `ticketing-web` access token, which MDR accepts on the panel's
 *   routes only (ADR-0034 §1 as amended).
 * - **links to MDR screens**: MDR renders its router's `Link`; the Tickets app
 *   renders a cross-app link into MDR.
 *
 * The API's base URL is `@bluesphere/api-client`'s `configureApi`, which the
 * host calls once at start-up, as MDR's `main.tsx` does.
 */

import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

/** An MDR screen the panel links to. */
export type MdrTarget = { to: 'alert'; alertRef: string } | { to: 'detection'; detectionId: string };

export interface MdrPanelHost {
  /** A hook: the bearer token for MDR's API, or '' before sign-in. */
  useToken: () => string;
  /** A link to an MDR screen, with the panel's class names and children. */
  renderLink: (target: MdrTarget, props: { className?: string; children: ReactNode }) => ReactNode;
}

const HostContext = createContext<MdrPanelHost | null>(null);

export function MdrPanelProvider({ host, children }: { host: MdrPanelHost; children: ReactNode }) {
  return <HostContext.Provider value={host}>{children}</HostContext.Provider>;
}

function useHost(): MdrPanelHost {
  const host = useContext(HostContext);
  if (!host) throw new Error('The alert panel needs an MdrPanelProvider above it.');
  return host;
}

/** The bearer token for MDR's API, from the host. */
export function useMdrToken(): string {
  return useHost().useToken();
}

export function MdrLink({
  target,
  className,
  children,
}: {
  target: MdrTarget;
  className?: string;
  children: ReactNode;
}) {
  return <>{useHost().renderLink(target, { className, children })}</>;
}
